import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Button,
  CircularProgress,
  InputBase,
  Box,
  Alert,
  Typography,
  Tooltip,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Search, PlayArrow, AutoAwesome } from '@mui/icons-material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import LanguageIcon from '@mui/icons-material/Language';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import useStyles from './style';
import { spacing, styled } from '@mui/system';
import { CustomVoice, HeygenVoice } from '../../../../context/types';
import { THEME_COLORS } from '../../../../context/constants';
import { getVoiceSuggestionsForActor, VoiceSuggestion } from '../../../../client/api';

const SearchBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#f1f1f1',
  border: `1px solid ${theme.palette.divider}`,
  padding: '2px 10px',
  borderRadius: '14px',
  marginBottom: '10px',
  maxWidth: '300px',
}));

interface VoiceSelectionModalProps {
  open: boolean;
  onClose: () => void;
  heygenVoices: Array<HeygenVoice>;
  // Selection callback. Receives the voice id plus optional meta so the parent
  // can populate display name / preview url without an extra lookup — important
  // for custom (voice-design) voices that aren't in the HeyGen v3 library list.
  onSelectVoice: (
    voiceId: string,
    meta?: { name: string; preview_audio_url: string | null }
  ) => void;
  actorId?: string;
  actorName?: string;
  customVoices?: CustomVoice[];
  customVoicesLoading?: boolean;
  customVoicesError?: string | null;
  onRefreshCustomVoices?: () => void;
}

const VoiceSelectionModal: React.FC<VoiceSelectionModalProps> = ({
  open,
  onClose,
  heygenVoices,
  onSelectVoice,
  actorId,
  actorName,
  customVoices = [],
  customVoicesLoading = false,
  customVoicesError = null,
  onRefreshCustomVoices,
}) => {
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null); // Track audio instance
  const [activeLanguage, setActiveLanguage] = useState<string>('All');
  const [activeGender, setActiveGender] = useState<string>('All');
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Suggested voices state
  const [suggestedVoices, setSuggestedVoices] = useState<VoiceSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

  // Fetch suggested voices when tab changes to suggestions or modal opens
  useEffect(() => {
    const fetchSuggestedVoices = async () => {
      if (activeTab === 1 && actorId && open) {
        setSuggestionsLoading(true);
        setSuggestionsError(null);
        try {
          const response = await getVoiceSuggestionsForActor(actorId, actorName);
          setSuggestedVoices(response.voice_suggestions || []);
        } catch (error: any) {
          console.error('Error fetching voice suggestions:', error);
          setSuggestionsError('Failed to load voice suggestions');
          setSuggestedVoices([]);
        } finally {
          setSuggestionsLoading(false);
        }
      }
    };

    fetchSuggestedVoices();
  }, [activeTab, actorId, actorName, open]);

  // Refresh custom voices each time the user enters the Your Voices tab so a
  // newly designed voice shows up without closing/reopening the modal.
  useEffect(() => {
    if (activeTab === 2 && open && onRefreshCustomVoices) {
      onRefreshCustomVoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, open]);

  // Reset tab when modal closes
  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setSuggestedVoices([]);
      setSuggestionsError(null);
    }
  }, [open]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setCurrentPage(1);
  };

  const handleSuggestedVoiceClick = (voice: VoiceSuggestion) => {
    onSelectVoice(voice.voice_id, {
      name: voice.voice_name,
      preview_audio_url: voice.audio_preview_url,
    });
    onClose();
  };

  const voicesPerPage = 20;

  // Extract unique languages and genders
  const availableLanguages = [
    'All',
    ...Array.from(new Set(heygenVoices.map((voice) => voice.language))),
  ];

  // Filter voices based on active filters and search query
  const filteredVoices = heygenVoices.filter((voice) => {
    // Only show voices with preview audio available
    if (!voice.preview_audio_url) return false;

    const matchesLanguage = activeLanguage === 'All' || voice.language === activeLanguage;
    const matchesGender = activeGender === 'All' || voice.gender === activeGender;
    const matchesSearchQuery = JSON.stringify(voice)
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesLanguage && matchesGender && matchesSearchQuery;
  });

  const totalPages = Math.ceil(filteredVoices.length / voicesPerPage);
  const currentVoices = filteredVoices.slice(
    (currentPage - 1) * voicesPerPage,
    currentPage * voicesPerPage
  );

  const handlePageChange = (direction: string) => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === 'previous' && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleSearchChange = (event: any) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (type: 'language' | 'gender', value: string) => {
    if (type === 'language') setActiveLanguage(value);
    if (type === 'gender') setActiveGender(value);
    setCurrentPage(1); // Reset pagination when filter changes
  };

  const handleLanguageChange = (language: string) => {
    setActiveLanguage(language);
    setCurrentPage(1); // Reset to first page after filtering
  };

  const handleGenderChange = (gender: string) => {
    setActiveGender(gender);
    setCurrentPage(1); // Reset to first page after filtering
  };

  const handleVoiceClick = async (voice: HeygenVoice) => {
    onSelectVoice(voice.voice_id, {
      name: voice.name,
      preview_audio_url: voice.preview_audio_url,
    });
    onClose();
  };

  const handleCustomVoiceClick = (voice: CustomVoice) => {
    onSelectVoice(voice.voice_id, {
      name: voice.name,
      preview_audio_url: voice.preview_audio_url,
    });
    onClose();
  };

  const playAudio = (audioUrl: string) => {
    stopAudio(); // Stop any currently playing audio
    const audio = new Audio(audioUrl);
    audio.play().catch((error) => console.error('Play failed: ', error));
    setCurrentAudio(audio); // Save reference to the currently playing audio
  };

  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0; // Reset audio to the start
      setCurrentAudio(null); // Clear current audio reference
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <div className={classes.dialog}>
        <DialogTitle>Select a Voice</DialogTitle>
        <DialogContent>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              mb: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 'bold',
                color: '#000',
              },
              '& .Mui-selected': {
                color: `${THEME_COLORS.darkYellow} !important`,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: THEME_COLORS.darkYellow,
              },
            }}
          >
            <Tab label="Voice Library" />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AutoAwesome sx={{ fontSize: 18 }} />
                  Suggested Voices
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RecordVoiceOverIcon sx={{ fontSize: 18 }} />
                  Your Voices
                </Box>
              }
            />
          </Tabs>

          {/* Voice Library Tab */}
          {activeTab === 0 && (
            <>
              {isLoading || heygenVoices.length === 0 ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    Loading voices...
                  </Typography>
                </div>
              ) : (
                <>
                  {/* Search Bar */}
                  <Grid container sx={{ alignItems: 'center' }}>
                    <SearchBar sx={{ marginRight: '4px' }}>
                      <InputBase
                        placeholder="Search voices..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        fullWidth
                      />
                      <IconButton>
                        <Search />
                      </IconButton>
                    </SearchBar>
                    <Typography
                      variant="body2"
                      style={{ fontSize: '12px', color: THEME_COLORS.darkYellow }}
                    >
                      Hint: you can search for voice name, gender, language, nationality, style...
                    </Typography>
                  </Grid>
                  <Grid>
                    <div className={classes.filters}>
                      {/* Gender Filter */}
                      {[
                        { value: 'All', label: 'All' },
                        { value: 'male', label: 'Male' },
                        { value: 'female', label: 'Female' },
                      ].map(({ value, label }) => (
                        <Chip
                          key={value}
                          label={label}
                          clickable
                          color={activeGender === value ? 'primary' : 'default'}
                          onClick={() => handleGenderChange(value)}
                        />
                      ))}

                      {/* Language Filter Dropdown */}
                      <Chip
                        label="Language"
                        clickable
                        onClick={() => setLanguageDropdownOpen((prev) => !prev)}
                        color={activeLanguage !== 'All' ? 'primary' : 'default'}
                      />
                      {languageDropdownOpen && (
                        <Box className={classes.dropdown}>
                          {availableLanguages.map((language) => (
                            <Button
                              key={language}
                              onClick={() => {
                                handleLanguageChange(language);
                                setLanguageDropdownOpen(false);
                              }}
                              style={{
                                textAlign: 'left',
                                width: '100%',
                                color:
                                  activeLanguage === language ? THEME_COLORS.darkYellow : 'inherit',
                              }}
                            >
                              {language}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </div>
                  </Grid>

                  {/* Voice List */}
                  <Grid container spacing={2} sx={{ marginTop: '8px' }}>
                    {currentVoices.map((voice) => (
                      <Grid item xs={12} sm={6} md={4} key={voice.voice_id}>
                        <Tooltip title={`${voice.name} • ${voice.language} • ${voice.gender}`}>
                          <div
                            className={classes.voiceCard}
                            onClick={() => {
                              handleVoiceClick(voice);
                              stopAudio();
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                flexWrap: 'wrap',
                                mb: 0.5,
                                minHeight: 22,
                              }}
                            >
                              {voice.support_pause && (
                                <Tooltip title="Supports SSML pause/break tags">
                                  <PauseCircleOutlineIcon
                                    sx={{ fontSize: 18, color: THEME_COLORS.darkYellow }}
                                  />
                                </Tooltip>
                              )}
                              {voice.support_locale && (
                                <Tooltip title="Supports locale variants">
                                  <LanguageIcon
                                    sx={{ fontSize: 18, color: THEME_COLORS.darkYellow }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            <div className={classes.voiceInfo}>
                              <span>
                                {voice.name.length > 10 ? `${voice.name.slice(0, 10)}...` : voice.name}
                              </span>
                              <div className={classes.soundWave} />

                              {/* Play Button */}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (voice.preview_audio_url) playAudio(voice.preview_audio_url);
                                }}
                              >
                                <PlayArrow />
                              </IconButton>
                            </div>
                          </div>
                        </Tooltip>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Pagination Controls */}
                  <div className={classes.paginationControls}>
                    <Button
                      variant="contained"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange('previous')}
                    >
                      Previous
                    </Button>
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="contained"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange('next')}
                    >
                      Next
                    </Button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Suggested Voices Tab */}
          {activeTab === 1 && (
            <>
              {suggestionsLoading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress />
                </div>
              ) : suggestionsError ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="error">{suggestionsError}</Typography>
                </Box>
              ) : suggestedVoices.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <AutoAwesome sx={{ fontSize: 48, color: THEME_COLORS.gray, mb: 2 }} />
                  <Typography color="textSecondary">
                    No voice suggestions available for this actor.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Try searching in the Voice Library tab.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    These voices are suggested based on the actor's name. Click to select.
                  </Typography>
                  <Grid container spacing={2}>
                    {suggestedVoices.map((voice) => (
                      <Grid item xs={12} sm={6} md={4} key={voice.voice_id}>
                        <Tooltip title={voice.voice_name}>
                          <div
                            className={classes.voiceCard}
                            onClick={() => {
                              handleSuggestedVoiceClick(voice);
                              stopAudio();
                            }}
                          >
                            <div className={classes.voiceInfo}>
                              <span>
                                {voice.voice_name.length > 10
                                  ? `${voice.voice_name.slice(0, 10)}...`
                                  : voice.voice_name}
                              </span>
                              <div className={classes.soundWave} />

                              {/* Play Button */}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  playAudio(voice.audio_preview_url);
                                }}
                              >
                                <PlayArrow />
                              </IconButton>
                            </div>
                          </div>
                        </Tooltip>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </>
          )}

          {/* Your Voices Tab (voice-design) */}
          {activeTab === 2 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Voices you've designed in Voice Design.
                </Typography>
                <Tooltip title="Refresh">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onRefreshCustomVoices?.()}
                      disabled={customVoicesLoading || !onRefreshCustomVoices}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {customVoicesLoading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress />
                </div>
              ) : customVoicesError ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="error">{customVoicesError}</Typography>
                </Box>
              ) : customVoices.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <RecordVoiceOverIcon
                    sx={{ fontSize: 48, color: THEME_COLORS.gray, mb: 2 }}
                  />
                  <Typography color="textSecondary">No voice yet</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                    Design your own voice from a natural language prompt in Voice Design.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open('/voice-design', '_blank')}
                    sx={{
                      backgroundColor: THEME_COLORS.darkYellow,
                      '&:hover': { backgroundColor: THEME_COLORS.darkYellow },
                    }}
                  >
                    Open Voice Design
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {customVoices.map((voice) => (
                    <Grid item xs={12} sm={6} md={4} key={voice.voice_id}>
                      <Tooltip
                        title={`${voice.name} • ${voice.language} • ${voice.gender}`}
                      >
                        <div
                          className={classes.voiceCard}
                          onClick={() => {
                            handleCustomVoiceClick(voice);
                            stopAudio();
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              flexWrap: 'wrap',
                              mb: 0.5,
                              minHeight: 22,
                            }}
                          >
                            {voice.support_pause && (
                              <Tooltip title="Supports SSML pause/break tags">
                                <PauseCircleOutlineIcon
                                  sx={{ fontSize: 18, color: THEME_COLORS.darkYellow }}
                                />
                              </Tooltip>
                            )}
                            {voice.support_locale && (
                              <Tooltip title="Supports locale variants">
                                <LanguageIcon
                                  sx={{ fontSize: 18, color: THEME_COLORS.darkYellow }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                          <div className={classes.voiceInfo}>
                            <span>
                              {voice.name.length > 10
                                ? `${voice.name.slice(0, 10)}...`
                                : voice.name}
                            </span>
                            <div className={classes.soundWave} />

                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (voice.preview_audio_url)
                                  playAudio(voice.preview_audio_url);
                              }}
                            >
                              <PlayArrow />
                            </IconButton>
                          </div>
                        </div>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </DialogContent>
      </div>
    </Dialog>
  );
};

export default VoiceSelectionModal;
